<template>
  <ion-page>
    <!-- Fixed header -->
    <div class="fixed-header">
      <b-slide-header />
    </div>

    <ion-content
      ref="contentRef"
      :fullscreen="false"
      :scroll-y="true"
      class="goals-page-content"
    >
      <!-- Home button -->
      <ion-fab slot="fixed" class="top-navigation">
        <ion-fab-button @click="goHome">
          <ion-icon :icon="homeSharp" />
        </ion-fab-button>
      </ion-fab>

      <!-- Loading indicators -->
      <ion-loading :is-open="loading" message="Hämtar värden..." />
      <ion-loading :is-open="saving" message="Sparar mål..." />

      <!-- Limit reached dialog -->
      <ion-alert
        :is-open="showLimitDialog"
        header="Begränsning"
        message="Du kan bara förbättra 3 områden åt gången. Ta bort ett befintligt val för att lägga till ett nytt."
        :buttons="['OK']"
        @didDismiss="showLimitDialog = false"
      ></ion-alert>

      <!-- Incomplete selection dialog -->
      <ion-alert
        :is-open="showIncompleteDialog"
        header="Ofullständig val"
        message="Du måste välja exakt 3 områden att förbättra för att fortsätta."
        :buttons="['OK']"
        @didDismiss="showIncompleteDialog = false"
      ></ion-alert>

      <!-- Three goals selected dialog -->
      <ion-alert
        :is-open="showThreeGoalsDialog"
        header="Dina valda mål"
        sub-header="Du har valt 3 områden att förbättra:"
        :message="getThreeGoalsMessage()"
        :buttons="[
          {
            text: 'Ändra',
            role: 'cancel',
            handler: () => {
              showThreeGoalsDialog = false;
            }
          },
          {
            text: 'Fortsätt',
            handler: () => {
              showThreeGoalsDialog = false;
              save();
            }
          }
        ]"
        @didDismiss="showThreeGoalsDialog = false"
      ></ion-alert>

      <!-- Main content wrapper -->
      <div class="content-wrapper">
        <h2 class="page-title pt-4">
          Här är listan på det du sagt att du vill förbättra.
        </h2>

        <p v-if="loading" class="loading-state">
          <ion-spinner />
          <span class="loading-text">Laddar mål...</span>
        </p>

        <p v-else-if="goals.length === 0" class="empty-state">
          Du har inte valt något att förbättra.
        </p>

        <template v-else>
          <p class="instruction-text max-w-6xl">
            Välj MAX 3 områden som du vill förbättra. 
          </p>

          <!-- Selection counter -->
          <div class="selection-counter">
            <span class="counter-text">
              Välj {{ selected.length }}-3 områden att förbättra!
            </span>
          </div>

          <!-- Goals list -->
          <div class="goals-list">
            <div
              v-for="factor in goals"
              :key="factor.category_id"
              class="goal-section flex gap-2 justify-between"
              :class="{ 'goal-selected': selected.includes(factor.category_id) }"
            >
              <!-- Labels for this specific factor -->
              <div class="labels-row flex justify-center items-center">
                <template v-for="i in 5" :key="i">
                  <div
                    v-if="i === 1 || factor[`label${i}`]"
                    class="bg-transparent !shadow-none label-item"
                  >
                    <b v-if="i === 1" class="text-sm md:text-lg">{{ factor.label }}</b>
                    <span v-else class="">{{ factor[`label${i}`] }}</span>
                  </div>
                </template>
              </div>

              <!-- Button row for this specific factor -->
              <div class="goal-item max-w-[250px] w-full">
                <div class="w-full flex">
                  <div
                    class="flex justify-center space-x-2 items-center text-base my-4 w-[30%] mx-auto"
                  >
                    <div class="flex flex-col items-center">
                      <div class="flex space-x-3 text-[18px]">
                        <span
                          :class="[
                            'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition border cursor-pointer',
                            bgColors[1],
                            !loading &&
                              (String(factor?.risk_value || factor?.value) ===
                                '1' ||
                              String(visions[factor.category_id]) === '1' ||
                              String(activeButtons[factor.category_id]) === '1'
                                ? 'scale-150'
                                : ''),
                          ]"
                          @click="handleClick(factor, 1)"
                          >1</span
                        >
                        <span
                          :class="[
                            'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition cursor-pointer',
                            bgColors[2],
                            !loading &&
                              (String(factor?.risk_value || factor?.value) ===
                                '2' ||
                              String(visions[factor.category_id]) === '2' ||
                              String(activeButtons[factor.category_id]) === '2'
                                ? 'scale-150'
                                : ''),
                          ]"
                          @click="handleClick(factor, 2)"
                          >2</span
                        >
                        <span
                          :class="[
                            'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition cursor-pointer',
                            bgColors[3],
                            !loading &&
                              (String(factor?.risk_value || factor?.value) ===
                                '3' ||
                              String(visions[factor.category_id]) === '3' ||
                              String(activeButtons[factor.category_id]) === '3'
                                ? 'scale-150'
                                : ''),
                          ]"
                          @click="handleClick(factor, 3)"
                          >3</span
                        >
                        <span
                          :class="[
                            'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition cursor-pointer',
                            bgColors[4],
                            !loading &&
                              (String(factor?.risk_value || factor?.value) ===
                                '4' ||
                              String(visions[factor.category_id]) === '4' ||
                              String(activeButtons[factor.category_id]) === '4'
                                ? 'scale-150'
                                : ''),
                          ]"
                          @click="handleClick(factor, 4)"
                          >4</span
                        >
                        <span
                          :class="[
                            'flex items-center justify-center w-5 h-5 outline outline-1 rounded-full text-sm transition cursor-pointer',
                            bgColors[5],
                            !loading &&
                              (String(factor?.risk_value || factor?.value) ===
                                '5' ||
                              String(visions[factor.category_id]) === '5' ||
                              String(activeButtons[factor.category_id]) === '5'
                                ? 'scale-150'
                                : ''),
                          ]"
                          @click="handleClick(factor, 5)"
                          >5</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Spacer for navigation area -->
        <div class="navigation-spacer"></div>
      </div>

      <!-- Fixed navigation area -->
      <div class="navigation-area">
        <div class="scroll-button" v-if="showScrollButton" @click="scrollDown">
          <b-icon-button>
            <arrow-down />
          </b-icon-button>
        </div>
        <div class="navigation-buttons">
          <div class="hidden lg:block"></div>
          <b-button @click="goBack" look="secondary">Tillbaka</b-button>
          <b-button @click="handleNext">Nästa</b-button>
          <div class="hidden lg:block"></div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import {
  IonContent,
  IonFab,
  IonFabButton,
  IonLoading,
  IonSpinner,
  IonPage,
  IonIcon,
  IonAlert,
  useIonRouter,
} from '@ionic/vue';
import BSlideHeader from '@/components/BSlideHeader.vue';
import Flag from '@/components/illustrations/Flag.vue';
import BIconButton from '@/components/BIconButton.vue';
import ArrowDown from '@/components/icons/ArrowDown.vue';
import { provide, ref, onMounted, watch, nextTick, onBeforeUnmount } from 'vue';
import { homeSharp } from 'ionicons/icons';
import { useStore } from '@/stores/main';

const router = useIonRouter();
const store = useStore();
const loading = ref(false);
const saving = ref(false);
const selected = ref<number[]>([]);
const goals = ref<any[]>([]);
const visions = ref<Record<number, number>>({});
const activeButtons = ref<Record<number, number>>({});
const contentRef = ref<any>(null);
const showScrollButton = ref(false);
const showLimitDialog = ref(false);
const showThreeGoalsDialog = ref(false);
const showIncompleteDialog = ref(false);

const bgColors = [
  'bg-white',
  'bg-red-400',
  'bg-orange-400',
  'bg-amber-300',
  'bg-lime-300',
  'bg-green-400',
];

const emit = defineEmits<{
  (e: 'visionUpdated', data: { id: number; vision: number }): void;
}>();

provide('pageData', {
  number: 3,
  title: 'Mina mål',
  icon: Flag,
});
provide('colorScheme', 'blue');

const getButtonColor = (factor: any, buttonNumber: number) => {
  const activeButton = activeButtons.value[factor.category_id] || factor.value;

  if (buttonNumber <= activeButton && buttonNumber > factor.value) {
    return 'rgb(50, 158, 50)'; // Green color for buttons selected to increase to
  } else if (activeButton > factor.value && buttonNumber <= factor.value) {
    // When increasing, all buttons up to the factor value should be green
    return 'rgb(50, 158, 50)'; // Green for all buttons up to factor value when increasing
  } else if (buttonNumber <= factor.value) {
    // Only when not increasing (activeButton === factor.value)
    if (factor.value < 3) {
      return '#8d3e79'; // Purple color for factor value < 3
    } else {
      return 'rgb(50, 158, 50)'; // Green for factor value >= 3
    }
  }
  return '#E8D5F0'; // Default color for unselected buttons
};

const getThreeGoalsMessage = () => {
  const selectedGoals = goals.value.filter(goal => 
    selected.value.includes(goal.category_id)
  );
  
  let message = '';
  selectedGoals.forEach((goal, index) => {
    const currentValue = goal.risk_value || goal.value;
    const targetValue = visions.value[goal.category_id];
    message += `${index + 1}. ${goal.label}: ${currentValue} → ${targetValue}<br>`;
    if (index < selectedGoals.length - 1) {
      message += '<br>';
    }
  });
  
  return message;
};

const handleClick = (factor: any, buttonNumber: number) => {
  if (buttonNumber < factor.value) {
    if (selected.value.includes(factor.category_id)) {
      selected.value = selected.value.filter(id => id !== factor.category_id);
      delete activeButtons.value[factor.category_id];
      delete visions.value[factor.category_id];
    }
    return;
  }

  if (buttonNumber === factor.value) {
    if (selected.value.includes(factor.category_id)) {
      selected.value = selected.value.filter(id => id !== factor.category_id);
      delete activeButtons.value[factor.category_id];
      delete visions.value[factor.category_id];
    }
    return;
  }

  // Check if trying to add a new goal and already at limit
  if (!selected.value.includes(factor.category_id) && selected.value.length >= 3) {
    showLimitDialog.value = true;
    return;
  }

  emit('visionUpdated', { id: factor.category_id, vision: buttonNumber });
  visions.value[factor.category_id] = buttonNumber;

  if (buttonNumber === 1 && selected.value.includes(factor.category_id)) {
    selected.value = selected.value.filter(id => id !== factor.category_id);
    delete activeButtons.value[factor.category_id];
  } else {
    if (!selected.value.includes(factor.category_id)) {
      selected.value.push(factor.category_id);
    }
    activeButtons.value[factor.category_id] = buttonNumber;
  }
};

const handleNext = () => {
  if (selected.value.length < 1) {
    showIncompleteDialog.value = true;
  } else if (selected.value.length === 3) {
    showThreeGoalsDialog.value = true;
  } else {
    save();
  }
};

const checkScrollNeeded = () => {
  if (contentRef.value && contentRef.value.$el) {
    const contentEl = contentRef.value.$el;
    showScrollButton.value =
      contentEl.scrollHeight > contentEl.clientHeight + 50;
  }
};

const scrollDown = () => {
  if (contentRef.value) {
    contentRef.value.scrollToBottom(300);
  }
};

const fetchGoals = async () => {
  loading.value = true;
  try {
    goals.value = await store.fetchGoals();
    const filteredGoals = goals.value.filter(f => f.selected === 1);
    selected.value = filteredGoals.map(factor => factor.category_id);
    filteredGoals.forEach(goal => {
      const categoryId = goal.category_id;
      const vision = parseInt(goal.vision, 10);
      visions.value[categoryId] = vision;
      activeButtons.value[categoryId] = vision;
    });
  } finally {
    loading.value = false;
    await nextTick();
    setTimeout(() => {
      checkScrollNeeded();
    }, 200);
  }
};

onMounted(async () => {
  // Refresh profile from server to get latest state (handles cross-browser sync)
  const hasActiveProfile = await store.refreshActiveProfile();
  if (!hasActiveProfile) {
    // No active profile - redirect to home
    window.location.href = '/home';
    return;
  }

  fetchGoals();
  window.addEventListener('resize', checkScrollNeeded);

  if (contentRef.value) {
    contentRef.value.$el.addEventListener('scroll', checkScrollNeeded);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkScrollNeeded);
  if (contentRef.value) {
    contentRef.value.$el.removeEventListener('scroll', checkScrollNeeded);
  }
});

// Watch for content changes to update scroll button visibility
watch(goals, () => {
  nextTick(() => {
    setTimeout(() => {
      checkScrollNeeded();
    }, 100);
  });
});

const goHome = () => router.navigate('/evaluate', 'root', 'replace');
const goBack = () => router.back();

async function save() {
  // Sort selected goals by their position in the original goals array
  // This ensures consistent Mål 1-3 ordering based on display order
  const goalOrderMap = new Map<number, number>();
  goals.value.forEach((g, idx) => goalOrderMap.set(g.category_id, idx));
  
  const sortedSelected = [...selected.value].sort((a, b) => {
    const aIdx = goalOrderMap.get(a) ?? 9999;
    const bIdx = goalOrderMap.get(b) ?? 9999;
    return aIdx - bIdx;
  });

  const newGoals = sortedSelected.map(category => ({
    category_id: category,
    vision: visions.value[category],
  }));

  saving.value = true;
  try {
    await store.updateGoals(newGoals);
    const firstSelected = sortedSelected[0];
    if (firstSelected) {
      const ids = sortedSelected.join(',');
      window.location.href = `/plan/${firstSelected}/actions?selected=${encodeURIComponent(ids)}`;
    } else {
      router.push('/plan/prelude');
    }
  } finally {
    saving.value = false;
  }
}

</script>

<style scoped>
/* Fixed header styles */
.fixed-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  background-color: #f3fffe;
  height: 80px;
}

/* Ionic content styles */
.goals-page-content {
  --background: #f3fffe;
  --padding-top: 80px;
  --padding-bottom: 0;
  position: relative;
}

/* Home button positioning */
.top-navigation {
  position: fixed;
  right: 0.5rem;
  top: 90px;
  z-index: 100;
}

/* Content wrapper */
.content-wrapper {
  padding: 20px 2rem 0;
  min-height: 100%;
}

.page-title {
  font-weight: 300;
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.loading-state,
.empty-state {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 2rem 0;
}

.loading-text {
  color: #3b82f6;
  font-weight: 500;
}

.instruction-text {
  font-size: 1rem;
  margin-bottom: 1.5rem;
  line-height: 1.5;
  color: #4a6572;
}

/* Selection counter */
.selection-counter {
  margin-bottom: 1rem;
  padding: 0.5rem 1rem;
  background-color: #e3f2fd;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
}

.counter-text {
  font-weight: 600;
  color: #1976d2;
  font-size: 0.9rem;
}

/* Scale header */
.scale-header {
  display: flex;
  width: 100%;
  max-width: 600px;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.scale-header-item {
  flex: 1;
  text-align: center;
  font-size: 1.25rem;
  font-weight: bold;
  color: #2c3e50;
}

/* Goals list */
.goals-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 600px;
}

.goal-section {
  padding: 1rem;
  border-radius: 12px;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.goal-section.goal-selected {
  background-color: #f0f8ff;
  border: 2px solid #2196f3;
  box-shadow: 0 4px 8px rgba(33, 150, 243, 0.2);
}

.goal-item {
  display: flex;
  gap: 0.125rem;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Scale buttons */
.scale-button {
  flex: 1;
  min-height: 50px;
  padding: 0.5rem;
  border: none;
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scale-button:hover {
  transform: scale(1.05);
  z-index: 2;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.scale-button:active {
  transform: scale(0.95);
}

.first-button {
  border-radius: 12px 0 0 12px;
}

.last-button {
  border-radius: 0 12px 12px 0;
}

.button-label {
  display: block;
  width: 100%;
  word-break: break-word;
  line-height: 1.2;
}

/* Navigation spacer */
.navigation-spacer {
  height: 120px;
}

/* Fixed navigation area */
.navigation-area {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 1.5rem 2rem;
  background-color: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  z-index: 10;
}

@media (min-width: 1024px) {
  .navigation-area {
    background-color: #a1cdc5;
  }
}

.navigation-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

@media (min-width: 1024px) {
  .navigation-buttons {
    grid-template-columns: 2fr 0.5fr 0.5fr 0.1fr;
  }
}

/* Scroll button */
.scroll-button {
  position: absolute;
  bottom: 100%;
  right: 2rem;
  margin-bottom: 1rem;
  z-index: 11;
}

@media (min-width: 768px) {
  .scroll-button {
    right: 3rem;
  }
}

@media (min-width: 1024px) {
  .scroll-button {
    right: 7rem;
  }
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .scale-button {
    font-size: 0.8rem;
    min-height: 40px;
  }

  .scale-header-item {
    font-size: 1rem;
  }
}

/* iOS scrolling improvements */
ion-content {
  --overflow: auto;
}

ion-content::part(scroll) {
  overscroll-behavior: auto;
  -webkit-overflow-scrolling: touch;
  overflow-y: auto;
}

/* Prevent accidental highlighting */
* {
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
}

button {
  -webkit-user-select: none;
  user-select: none;
}
</style>